import { supabase } from '@/lib/supabase/client';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import type { AISuggestion } from '@/types/ai-suggestions';
import type { Task } from '@/types/tasks';

type FetchSuggestionsResponse = {
  suggestions: AISuggestion[];
  remainingRequests: number;
};

/**
 * AISuggestionsManager handles AI-generated suggestions for the home screen.
 * Communicates with the /api/ai-suggestions endpoint for generation and rate limiting.
 */
export class AISuggestionsManager {
  private static instance: AISuggestionsManager | null = null;

  static getInstance(): AISuggestionsManager {
    if (!AISuggestionsManager.instance) {
      AISuggestionsManager.instance = new AISuggestionsManager();
    }
    return AISuggestionsManager.instance;
  }

  /**
   * Fetch AI suggestions for the given categories.
   * Calls the server-side API which handles generation and rate limiting.
   */
  async fetchSuggestions(): Promise<FetchSuggestionsResponse> {
    const response = await authenticatedFetch.fetch('/api/ai-suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        categories: ['appointments', 'social_events', 'life_admin', 'anniversaries', 'something_fun'],
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMsg = (errorData as { error?: string })?.error ?? `Request failed (${response.status})`;
      throw new Error(errorMsg);
    }

    return (await response.json()) as FetchSuggestionsResponse;
  }

  /**
   * Fetch remaining requests for today without burning a request.
   */
  async fetchRemainingRequests(): Promise<number | null> {
    try {
      const response = await authenticatedFetch.fetch('/api/ai-suggestions');

      if (!response.ok) {
        console.warn('[AISuggestionsManager] Failed to fetch remaining requests:', response.status);
        return null;
      }

      const data = (await response.json()) as { remainingRequests: number };
      return data.remainingRequests;
    } catch (error) {
      console.warn('[AISuggestionsManager] Could not reach suggestions API:', error);
      return null;
    }
  }

  /**
   * Dismiss a suggestion so it doesn't appear again.
   */
  async dismissSuggestion(suggestionId: string): Promise<void> {
    console.log('[AISuggestionsManager] Dismissed suggestion:', suggestionId);
  }

  /**
   * Convert an AI suggestion into a task and add it to the user's task list.
   */
  async convertToTask(
    suggestion: AISuggestion,
    userId: string,
  ): Promise<Task | null> {
    try {
      const { data: existingTasks, error: fetchError } = await supabase
        .from('tasks')
        .select('order_index')
        .eq('user_id', userId)
        .order('order_index', { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error('[AISuggestionsManager] Error fetching tasks:', fetchError);
        throw fetchError;
      }

      const maxOrderIndex = existingTasks && existingTasks.length > 0
        ? existingTasks[0].order_index
        : 0;

      const { data: defaultList, error: defaultListError } = await supabase
        .from('task_lists')
        .select('id')
        .eq('owner_user_id', userId)
        .eq('is_default', true)
        .maybeSingle();

      if (defaultListError || !defaultList) {
        console.error('[AISuggestionsManager] Could not resolve default list:', defaultListError);
        throw defaultListError ?? new Error('Default list not found.');
      }

      const newTask: Record<string, unknown> = {
        user_id: userId,
        list_id: defaultList.id,
        title: suggestion.title,
        description: suggestion.description,
        completed: false,
        wont_do: false,
        order_index: maxOrderIndex + 1,
        metadata: {
          labels: ['ai-suggested'],
        },
      };

      if (suggestion.actions && suggestion.actions.length > 0) {
        // Client-safe subtask id, matching ChatTaskCreator's `sub-<ts>-<i>`
        // format. Hermes has no crypto.randomUUID(), so the previous call threw
        // and surfaced as a generic "Failed" on every Add Task.
        newTask.subtasks = suggestion.actions.map((action, index) => ({
          id: `sub-${Date.now()}-${index}`,
          title: action,
          completed: false,
        }));
      }

      if (suggestion.suggestedDate ?? suggestion.date) {
        newTask.date = suggestion.suggestedDate ?? suggestion.date;
      }
      if (suggestion.metadata?.category) {
        newTask.category = suggestion.metadata.category;
      }

      const { data, error } = await supabase
        .from('tasks')
        .insert(newTask)
        .select()
        .single();

      if (error) {
        console.error('[AISuggestionsManager] Error creating task:', error);
        throw error;
      }

      await this.dismissSuggestion(suggestion.id);

      const row = data as Record<string, unknown>;
      const task: Task = {
        id: row.id as string,
        userId: row.user_id as string,
        listId: row.list_id as string,
        title: row.title as string,
        completed: row.completed as boolean,
        wontDo: (row.wont_do as boolean) ?? undefined,
        orderIndex: row.order_index as number,
        description: (row.description as string) ?? undefined,
        date: (row.date as string) ?? undefined,
        category: (row.category as string) ?? undefined,
        metadata: (row.metadata as Task['metadata']) ?? undefined,
        createdAt: row.created_at as string,
        updatedAt: row.updated_at as string,
      };
      return task;
    } catch (error) {
      console.error('[AISuggestionsManager] Error converting suggestion to task:', error);
      return null;
    }
  }
}
